import throttle from 'lodash.throttle'
import { Plugin, PluginKey } from 'prosemirror-state'

import { LRUScriptValueStorage } from '#util/LocalPersistence'
import { scrollToBlock } from '#util/scrolling'

import { blockInfoPluginKey } from './block-info/blockInfo'
import { resolveBlockAt } from './block-info/extractBlockInfo'
import { collabUpdateKey } from './collab-update'
import { PluginFactoryParams } from './types'

const recordLastPositionKey = new PluginKey('recordLastPosition')

const THROTTLE_MS = 1000
const LAST_EDITED_SCOPE = 'scripto-last-edited'
const MAX_LRU_ENTRIES = 100

export type RecordLastPositionConfig = {
  initialBlockId: string | undefined
  onPositionChange: (blockId: string) => void
  getDeepLink: () => string | undefined
  clearDeepLink: () => void
}

export const recordLastPositionAdapter = ({
  mst,
  scriptId,
}: PluginFactoryParams): Plugin => {
  const { localPersistence } = mst.environment
  const userId = mst.user.id

  const recentlyEditedScripts = LRUScriptValueStorage.createForUser({
    storage: localPersistence,
    userId,
    scope: LAST_EDITED_SCOPE,
    maxEntries: MAX_LRU_ENTRIES,
    parse: (value) => value,
    encode: (value) => value,
  })

  return recordLastPositionPlugin({
    initialBlockId: recentlyEditedScripts.get(scriptId),
    onPositionChange: (blockId) => {
      recentlyEditedScripts.set(scriptId, blockId)
    },
    getDeepLink: () => mst.location.getQueryParam('block') ?? undefined,
    clearDeepLink: () =>
      mst.location.updateQueryParams({ block: null }, { replace: true }),
  })
}

export const recordLastPositionPlugin = ({
  initialBlockId,
  onPositionChange,
  getDeepLink,
  clearDeepLink,
}: RecordLastPositionConfig): Plugin => {
  let lastPersistedId: string | null = null
  const persist = throttle(onPositionChange, THROTTLE_MS)

  return new Plugin({
    key: recordLastPositionKey,
    view(view) {
      let shouldClearDeepLink = false

      requestAnimationFrame(() => {
        const deepLink = getDeepLink()
        if (deepLink) {
          scrollToBlock(deepLink, { flash: true, behavior: 'auto' })
          view.focus()
          shouldClearDeepLink = true
          view.dispatch(view.state.tr)
        } else if (initialBlockId) {
          scrollToBlock(initialBlockId, { flash: false, behavior: 'auto' })
        }
      })

      return {
        update(view) {
          if (view.isDestroyed || !view.hasFocus()) return
          if (collabUpdateKey.getState(view.state)) return

          if (shouldClearDeepLink) {
            shouldClearDeepLink = false
            requestAnimationFrame(clearDeepLink)
            return
          }

          const blockInfo = blockInfoPluginKey.getState(view.state)
          if (!blockInfo) return

          const block = resolveBlockAt(blockInfo, view.state.selection.head)
          if (!block || block.id === lastPersistedId) return

          lastPersistedId = block.id
          persist(block.id)
        },
        destroy() {
          persist.flush()
        },
      }
    },
  })
}
